/**
 * LearningItemsPage — F5
 *
 * CRUD page for Learning Items. Learning items capture retros, experiments,
 * observations, customer feedback, incidents and other learnings that the
 * org wants to track and apply.
 *
 * Compliance: Phase F5 OMPRIKL Finishing Wave.
 *
 * Sheet Migration Wave 4b (AC-W4b-01): LearningItemFormDialog uses FormSheet<LearningSnapshot>.
 * The component is defined inline below so all form-field strings remain in this file and
 * unit-test source-scan assertions continue to pass.
 *
 * urlKey="edit-learning" for deep-link routing.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Alert } from '@/components/ui/Alert';
import { FormSheet } from '@/components/ui/FormSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { Plus, Lightbulb, Pencil, Trash2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import {
  useRorosLearning,
  useRorosLearningCreate,
  useRorosLearningUpdate,
  useRorosLearningDelete,
} from '@/hooks/roros/artifacts';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import type { RorosLearningItemDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';

const STRATEGY_DOMAIN_ID_INT = 1;
const DEFAULT_TARGET_ARTIFACT_ID = '00000000-0000-4000-a000-000000000001';
const LEARNING_URL_KEY = 'edit-learning';

// Source tag values must match createLearningItemSchema.source_tag enum exactly:
//   z.enum(['routine_run', 'cadence_review', 'external_observation'])
// v1.1.8 fix: previous values (retro/experiment/etc.) caused 400 ValidationError
// on every form submit because they were not in the server's Zod enum.
const SOURCE_TAG_OPTIONS = [
  { value: 'routine_run', label: t('Routine run') },
  { value: 'cadence_review', label: t('Cadence review') },
  { value: 'external_observation', label: t('External observation') },
];

const STATUS_OPTIONS = [
  { value: 'open', label: t('Open') },
  { value: 'applied', label: t('Applied') },
  { value: 'archived', label: t('Archived') },
];

const TARGET_ARTIFACT_TYPE_OPTIONS = [
  { value: 'routine', label: t('Routine') },
  { value: 'outcome', label: t('Outcome') },
  { value: 'metric', label: t('Metric') },
  { value: 'knowledge', label: t('Knowledge') },
  { value: 'principle', label: t('Principle') },
];

// ─── Snapshot type & diff (Wave 4b) ──────────────────────────────────────────

type LearningSnapshot = {
  title: string;
  description: string;
  domainId: string;
  sourceTag: string;
  status: string;
  targetArtifactType: string;
  targetArtifactId: string;
};

function diffLearningSnapshots(
  base: LearningSnapshot,
  cur: LearningSnapshot,
): Partial<Record<keyof LearningSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    domainId: base.domainId !== cur.domainId,
    sourceTag: base.sourceTag !== cur.sourceTag,
    status: base.status !== cur.status,
    targetArtifactType: base.targetArtifactType !== cur.targetArtifactType,
    targetArtifactId: base.targetArtifactId !== cur.targetArtifactId,
  };
}

// ─── LearningItemFormDialog (inline, Wave 4b) ─────────────────────────────────
//
// Inline here (not a separate file) so unit-test source-scan assertions on
// LearningItemsPage.tsx can verify that title, description, domain_id,
// source_tag, status, target_artifact_type, target_artifact_id are present.

interface LearningItemFormDialogProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  domains: RorosDomainDTO[];
  editingItem?: RorosLearningItemDTO;
  defaultDomainId: string;
  urlKey?: string;
  urlId?: string;
}

function LearningItemFormDialog({
  mode,
  isOpen,
  onClose,
  domains,
  editingItem,
  defaultDomainId,
  urlKey,
  urlId,
}: LearningItemFormDialogProps) {
  const createMutation = useRorosLearningCreate();
  const updateMutation = useRorosLearningUpdate();

  const domainOptions = useMemo(
    () => domains.map((d) => ({ value: d.id, label: d.name })),
    [domains],
  );

  // ─── Form state via useFormSnapshot ─────────────────────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<LearningSnapshot>({
    initialFactory: () =>
      mode === 'edit' && editingItem
        ? {
            title: editingItem.title,
            description: editingItem.description ?? '',
            domainId: editingItem.domainId ?? defaultDomainId,
            sourceTag: editingItem.sourceTag ?? 'routine_run',
            status: editingItem.status,
            targetArtifactType: editingItem.targetArtifactType ?? 'routine',
            targetArtifactId: editingItem.targetArtifactId ?? DEFAULT_TARGET_ARTIFACT_ID,
          }
        : {
            title: '',
            description: '',
            domainId: defaultDomainId,
            sourceTag: 'routine_run',
            status: 'open',
            targetArtifactType: 'routine',
            targetArtifactId: DEFAULT_TARGET_ARTIFACT_ID,
          },
    isOpen,
    depKey: editingItem?.id ?? null,
  });

  const { title, description, domainId, sourceTag, status, targetArtifactType, targetArtifactId } =
    state;

  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (isOpen) setError(null); }, [isOpen, editingItem?.id]);

  // ─── Validation & save ───────────────────────────────────────────────────
  const handleSave = () => {
    setError(null);
    if (!title.trim()) {
      setError(t('Title is required'));
      return;
    }

    if (mode === 'edit' && editingItem) {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        source_tag: sourceTag || undefined,
        status,
        target_artifact_type: targetArtifactType || undefined,
        target_artifact_id: targetArtifactId.trim() || undefined,
      };
      updateMutation.mutate(
        { domainId: editingItem.domainId, id: editingItem.id, payload: payload as any },
        {
          onSuccess: () => { showToast.success(t('Learning item updated')); onClose(); },
          onError: (err: any) => setError(err?.message || t('Failed to save learning item')),
        },
      );
    } else {
      const effectiveDomainId = domainId || defaultDomainId;
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        source_tag: sourceTag || undefined,
        status,
        target_artifact_type: targetArtifactType || 'routine',
        target_artifact_id: targetArtifactId.trim() || DEFAULT_TARGET_ARTIFACT_ID,
      };
      createMutation.mutate(
        { domainId: effectiveDomainId, payload: payload as any },
        {
          onSuccess: () => { showToast.success(t('Learning item created')); onClose(); },
          onError: (err: any) => setError(err?.message || t('Failed to save learning item')),
        },
      );
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormSheet<LearningSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? t('Edit Learning Item') : t('New Learning Item')}
      mode={mode}
      baseline={baseline}
      current={current}
      diff={diffLearningSnapshots}
      onSubmit={handleSave}
      isSubmitting={isPending}
      submitLabel={mode === 'edit' ? t('Save') : t('Create Learning Item')}
      submittingLabel={t('Saving…')}
      submitTestId="learning-save-button"
      width="md"
      urlKey={urlKey}
      urlId={urlId}
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" data-testid="learning-error">
            {error}
          </Alert>
        )}

        {/* 1. Title */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-title">
            {t('Title')} <span className="text-destructive">*</span>
          </label>
          <Input
            id="learning-title"
            value={title}
            onChange={(e) => setField('title', e.target.value)}
            placeholder={t('What did we learn?')}
            required
            data-testid="learning-title-input"
          />
        </div>

        {/* 2. Description */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-description">
            {t('Description')} <span className="text-destructive">*</span>
          </label>
          <Textarea
            id="learning-description"
            value={description}
            onChange={(e) => setField('description', e.target.value)}
            rows={3}
            data-testid="learning-description-input"
          />
        </div>

        {/* 3. Domain */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-domain">
            {t('Domain')}
          </label>
          <Select
            id="learning-domain"
            value={domainId}
            onChange={(e) => setField('domainId', e.target.value)}
            options={domainOptions}
            data-testid="learning-domain-select"
          />
        </div>

        {/* 4. Source Tag */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-source-tag">
            {t('Source Tag')}
          </label>
          <Select
            id="learning-source-tag"
            value={sourceTag}
            onChange={(e) => setField('sourceTag', e.target.value)}
            options={SOURCE_TAG_OPTIONS}
            data-testid="learning-source-tag-select"
          />
        </div>

        {/* 5. Status */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-status">
            {t('Status')}
          </label>
          <Select
            id="learning-status"
            value={status}
            onChange={(e) => setField('status', e.target.value)}
            options={STATUS_OPTIONS}
            data-testid="learning-status-select"
          />
        </div>

        {/* 6. Target Artifact Type */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-target-artifact-type">
            {t('Target Artifact Type')}
          </label>
          <Select
            id="learning-target-artifact-type"
            value={targetArtifactType}
            onChange={(e) => setField('targetArtifactType', e.target.value)}
            options={TARGET_ARTIFACT_TYPE_OPTIONS}
            data-testid="learning-target-artifact-type-select"
          />
        </div>

        {/* 7. Target Artifact ID */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="learning-target-artifact-id">
            {t('Target Artifact ID')}
          </label>
          <Input
            id="learning-target-artifact-id"
            value={targetArtifactId}
            onChange={(e) => setField('targetArtifactId', e.target.value)}
            placeholder={t('UUID of the target artifact')}
            data-testid="learning-target-artifact-id-input"
          />
        </div>
      </div>
    </FormSheet>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'applied': return 'success';
    case 'archived': return 'default';
    default: return 'warning';
  }
}

// ─── Learning row ─────────────────────────────────────────────────────────────

interface LearningRowProps {
  item: RorosLearningItemDTO;
  domains: RorosDomainDTO[];
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function LearningRow({ item, domains, canEdit, canDelete, onEdit, onDelete }: LearningRowProps) {
  const domain = domains.find((d) => d.id === item.domainId);
  return (
    <Card className="p-4" role="listitem" tabIndex={0} aria-label={item.title}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && canEdit) onEdit(); }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{item.title}</h3>
            <Badge variant={statusVariant(item.status)}>{t(item.status)}</Badge>
            {item.sourceTag && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{t(item.sourceTag)}</span>
            )}
            {item.applied && <Badge variant="success">{t('Applied')}</Badge>}
          </div>
          {item.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
            {domain && <span>{t('Domain')}: <span className="font-medium text-foreground">{domain.name}</span></span>}
            {item.targetArtifactType && (
              <span>{t('Target Artifact Type')}: <span className="font-medium text-foreground">{t(item.targetArtifactType)}</span></span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canEdit && <Button variant="ghost" size="sm" onClick={onEdit} aria-label={t('Edit')}><Pencil className="h-3.5 w-3.5" /></Button>}
          {canDelete && <Button variant="ghost" size="sm" onClick={onDelete} aria-label={t('Delete')} className="text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function LearningItemsPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RorosLearningItemDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [filterDomainId, setFilterDomainId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('open');

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_CREATE);
  const canEdit = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_EDIT);
  const canDelete = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_DELETE);

  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive);

  const strategyDomain = useMemo(
    () => domains.find((d) => d.domainId === STRATEGY_DOMAIN_ID_INT) ?? null,
    [domains],
  );

  const activeDomainId = filterDomainId || (strategyDomain?.id ?? (domains[0]?.id ?? ''));

  const learningParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterStatus && filterStatus !== 'all') p.status = filterStatus;
    return Object.keys(p).length > 0 ? p : undefined;
  }, [filterStatus]);

  const learningQuery = useRorosLearning(activeDomainId, learningParams);
  const items = (learningQuery.data ?? []) as RorosLearningItemDTO[];

  const deleteMutation = useRorosLearningDelete();

  const openCreate = useCallback(() => { setEditingItem(null); setIsFormOpen(true); }, []);
  const openEdit = useCallback((item: RorosLearningItemDTO) => { setEditingItem(item); setIsFormOpen(true); }, []);

  // ─── URL routing (?edit-learning=<id>) ─────────────────────────────────
  const getLearningId = useCallback((item: RorosLearningItemDTO) => item.id, []);
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: LEARNING_URL_KEY,
    items,
    getId: getLearningId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingItem(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    const item = items.find((i) => i.id === deleteTarget.id);
    if (!item) return;
    deleteMutation.mutate(
      { domainId: item.domainId, id: deleteTarget.id },
      {
        onSuccess: () => { showToast.success(t('Learning item deleted')); setDeleteTarget(null); },
        onError: (err) => showToast.error(err.message),
      },
    );
  }, [deleteTarget, deleteMutation, items]);

  const emptyCTA = canCreate ? (
    <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="learning-empty-cta">
      {t('Capture your first Learning')}
    </Button>
  ) : undefined;

  return (
    <RorosPage
      title={t('Learning Items')}
      icon={<Lightbulb className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Learning Items'), path: '/roros/artifacts/learning' }]}
      actions={
        canCreate ? (
          <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="new-learning-button">
            {t('New Learning Item')}
          </Button>
        ) : undefined
      }
    >
      <div role="group" className="flex flex-wrap gap-3 mb-4" aria-label={t('Learning filters')}>
        <div className="w-44">
          <Select
            id="learning-domain-filter"
            value={filterDomainId || 'all'}
            onChange={(e) => setFilterDomainId(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by domain')}
            options={[
              { value: 'all', label: t('All Domains') },
              ...domains.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            id="learning-status-filter"
            data-testid="learning-status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('Filter by status')}
            options={[
              { value: 'open', label: t('Open') },
              { value: 'applied', label: t('Applied') },
              { value: 'archived', label: t('Archived') },
              { value: 'all', label: t('All Statuses') },
            ]}
          />
        </div>
      </div>

      <QueryPage
        query={learningQuery as any}
        emptyMessage={t('No learning items yet')}
        emptyIcon={<Lightbulb className="h-8 w-8 text-muted-foreground" />}
        emptyAction={emptyCTA}
      >
        {(learningItems) => (
          <div className="space-y-3" role="list" aria-label={t('Learning Items')}>
            {(learningItems as RorosLearningItemDTO[]).map((item) => (
              <LearningRow
                key={item.id}
                item={item}
                domains={domains}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => openEdit(item)}
                onDelete={() => setDeleteTarget({ id: item.id, title: item.title })}
              />
            ))}
          </div>
        )}
      </QueryPage>

      {/* ─── Create/Edit FormSheet (Wave 4b) ────────────────────────────── */}
      <LearningItemFormDialog
        mode={editingItem ? 'edit' : 'create'}
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingItem(null); }}
        domains={domains}
        editingItem={editingItem ?? undefined}
        defaultDomainId={activeDomainId}
        urlKey={editingItem ? LEARNING_URL_KEY : undefined}
        urlId={editingItem?.id}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('Delete this learning item?')}
        description={deleteTarget ? `"${deleteTarget.title}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </RorosPage>
  );
}
